// The single gate between a caught error and user-visible copy. Everything a
// banner renders should pass through safeErrorMessage so raw exception text,
// response bodies, and stack-adjacent strings can never reach the UI.
import type { StripeError } from '@stripe/stripe-js';
import { ApiError, GENERIC_ERROR_MESSAGE } from '@/shared/api/apiClient';
import { logger } from '@/shared/utils/logger';

export { GENERIC_ERROR_MESSAGE };

// An Error whose message was written FOR the user (pre-flight validation,
// "the payment form is still loading", a Stripe.js message we re-throw).
// safeErrorMessage shows these verbatim; plain Errors it does not.
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

// ApiError messages are humanized by apiClient (code map or generic copy) and
// UserFacingError is authored copy — both safe to render. Anything else is
// internal text that belongs in the console, not in a banner.
export function safeErrorMessage(err: unknown, fallback: string = GENERIC_ERROR_MESSAGE): string {
  if (err instanceof ApiError || err instanceof UserFacingError) return err.message;
  logger.error('Unexpected error surfaced as generic copy:', err);
  return fallback;
}

// Stripe.js writes `message` for end users only on card_error / validation_error
// ("Your card was declined.", "Your card number is incomplete."). Every other
// type carries developer copy — e.g. confirming a PaymentIntent whose abandoned
// 3DS challenge detached its payment method yields a raw invalid_request_error
// paragraph about payment_method_data parameters.
const STRIPE_TYPE_MESSAGES: Record<string, string> = {
  api_connection_error: 'The payment service is temporarily unavailable. Please try again.',
  api_error: 'The payment service is temporarily unavailable. Please try again.',
  rate_limit_error: 'The payment service is temporarily unavailable. Please try again.',
  invalid_request_error: "We couldn't complete this payment attempt. Please try again.",
};

// The Stripe.js counterpart of safeErrorMessage: user-authored types pass
// through verbatim, everything else renders catalog copy while the raw details
// go to the console.
export function stripeErrorMessage(error: StripeError, fallback: string): string {
  if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
    return error.message;
  }
  logger.error('Stripe.js error surfaced as catalog copy:', error.type, error.code, error.message);
  return STRIPE_TYPE_MESSAGES[error.type] ?? fallback;
}
